import formats from './date/format';
import maybePluralize from 'mex_components/app/scripts/string/maybePluralize';
import { convertTime } from './buybox_utils';
import materialIconDateRange
  from '../templates/svg/material_icon_date_range.mustache';
import materialIconEventSeat from '../templates/svg/material_icon_event_seat.mustache';
import materialIconSchedule from '../templates/svg/material_icon_schedule.mustache';
import materialIconSupervisorAccount
  from '../templates/svg/material_icon_supervisor_account.mustache';
 
/**
 * Returns a seat remaining banner when there are limited seats left (less than 6).
 *
 * @param {Object} offerListing The offer for a parent item.
 */
const _seatsRemainingBanner = (offerListing) => {
  const seatsLeft = offerListing.available_quantity;
  if (seatsLeft && seatsLeft > 0 && seatsLeft < 6 ) {
    return {
      banner_class: 'item-offer--banner',
      banner_text: `Only ${maybePluralize(seatsLeft, 'spot')} left`,
      banner_type: 'seats_remaining_banner',
      icon: materialIconEventSeat
    };
  }
};

/**
 * Returns a count down banner text based on the difference between the
 * class start time and the current time.
 *
 * @param {Object} offerDate The Time when the class is about to start.
 */
const _bannerTextWithIcon = (offerDate) => {
  const parsedOfferDate = new Date(offerDate);
  const timeDiffInHours = (parsedOfferDate.getTime() - new Date().getTime()) / (1000 * 60 * 60);
  const daysDiff = timeDiffInHours / 24;
  if (timeDiffInHours >= 0 && timeDiffInHours < 24) {
    return { banner_text: 'Begins in less than 24 hours', icon: materialIconSchedule };
  } else if (timeDiffInHours >= 24 && timeDiffInHours < 48) {
    return { banner_text: 'Begins in less than 48 hours', icon: materialIconSchedule };
  } else if (timeDiffInHours >= 48 && daysDiff <= 7) {
    return { banner_text: 'Starts this week', icon: materialIconDateRange };
  }
};

/**
 * Returns a course count down banner when the schedule is coming up in between
 * a day and a week.
 *
 * @param {Object} offerListing The offer for a parent item.
 */
const _courseStartCountdownBanner = (offerListing) => {
  const offerDate = offerListing.schedule.meetings[0].time.split('/')[0];
  if (offerDate) {
    const bannerTextWithIcon = _bannerTextWithIcon(offerDate);
    if (bannerTextWithIcon) {
      return Object.assign({
        banner_class: 'item-offer--banner',
        banner_type: 'course_start_countdown_banner'
      }, bannerTextWithIcon);
    }
  }
};

/**
 * Returns VT+ enrollments nearest to 5 when the enrollments are greater than 10.
 *
 * @param {Integer} enrollments The enrollments for an offer.
 */
 const _VTPlusEnrollments = (enrollments) => {
  if (enrollments >= 10) {
    // round down to nearest 5 with a max. of 20
    return Math.min(((enrollments / 5) * 5), 20);
  }
};

/**
 * Returns free enrollments nearest to 500 when the enrollments are greater than 100.
 *
 * @param {Integer} enrollments The enrollments for an offer.
 */
const _freeEnrollments = (enrollments) => {
  if (enrollments >= 100) {
    // round down to nearest 50 with a max. of 500
    return Math.min(((enrollments / 50) * 50), 500);
  }
};

/**
 * Returns paid enrollments nearest to 5 when the enrollments are greater than 5.
 *
 * @param {Integer} enrollments The enrollments for an offer.
 */
const _paidEnrollments = (enrollments) => {
  if (enrollments >= 5) {
    // round down to nearest 5
    return (enrollments / 5) * 5;
  }
};

/**
 * Returns rounded enrollments with respect to the price.
 *
 * @param {Integer} price The price of an offer.
 * @param {Integer} enrollments The enrollments for an offer.
 */
const _roundedEnrollments = (price, enrollments, requiredSubscription) => {
  // Currently VT+ is the one and only required subscription, it will not
  // necessarily always going be the only 1.
  if (requiredSubscription && requiredSubscription.name === 'vt+') {
    return _VTPlusEnrollments(enrollments);
  } else if (price === 0) {
    return _freeEnrollments(enrollments);
  } else if (price > 0) {
    return _paidEnrollments(enrollments);
  }
};

/**
 * Returns students enrolled banner.
 *
 * @param {Object} offerListing The offer for a parent item.
 */
const _studentsEnrolledBanner = (offerListing, requiredSubscription) => {
  const price = offerListing.price.amount;
  const availableQuantity = offerListing.available_quantity;
  const capacity = offerListing.capacity;

  if (price >= 0 && availableQuantity && capacity) {
    const enrollments = capacity - availableQuantity;
    const roundedEnrollments = _roundedEnrollments(price, enrollments, requiredSubscription);

    if (roundedEnrollments >= 0 ) {
      return { 
        banner_class: 'item-offer--banner',
        banner_text:
         `${roundedEnrollments}+` +
          ` ${maybePluralize(roundedEnrollments, 'student').split(' ')[1]} already enrolled`,
        banner_type: 'students_enrolled_banner',
        icon: materialIconSupervisorAccount 
      };
    }
  }
};

/**
 * Returns a banner for an offer.
 *
 * @param {Object} offerListing The offer for a parent item.
 * @param {Object} requiredSubscription item required_subscription
 * eg: { "id": "199ff27d-55d4-4e51-a39a-2c3c6c023ac1", 
 *       "name": "vt+",
 *       "frequency": "monthly",
 *       "list_price": {
 *         "amount": 1900,
 *         "currency_code": "USD",
 *         "formatted": "$19.00"
 *       }
 *     }
 */
export const bannerInfo = (offerListing, requiredSubscription) => {
  const seatsRemainingBanner = _seatsRemainingBanner(offerListing);
  const courseStartCountdownBanner = _courseStartCountdownBanner(offerListing);
  const studentsEnrolledBanner = _studentsEnrolledBanner(offerListing, requiredSubscription);

  return seatsRemainingBanner || courseStartCountdownBanner || studentsEnrolledBanner;
};

/**
 * Returns list of excluded dates fom meeting schedule.
 *
 * @param {object} schedule The schedule for an offer.
 */
 export const excludedMeetingTimes = (schedule) => {
  if (schedule.dates_excluded && schedule.dates_excluded.length) {
    return { exluded_dates: schedule.dates_excluded.map((date) => 
      formats.longDate(new Date(date)))
    };
  }
};

/**
 * Returns the formatted session minutes or hours.
 *
 * @param {Integer} minutes The duration of a session in minutes.
 */
 const _sessionMinutesOrHours = (minutes) => {
  if (minutes < 60) {
    return `${minutes}min`;
  }

  if (minutes % 60 === 0) {
    return `${minutes / 60}hr`;
  }

  return `${Math.floor(minutes / 60)}hr ${minutes % 60}min`;
};

/**
 * Returns the formatted duration for an offer.
 *
 * @param {Integer} minutes The duration of a session in minutes.
 * @param {Integer} totalSessions The total number of sessions.
 */
export const formattedDuration = (minutes, totalSessions) => {
  return `${_sessionMinutesOrHours(minutes)} live` +
   ` ${maybePluralize(totalSessions, 'session').split(' ')[1]}`;
};

/**
 * Returns the formatted meeting schedule which includes the start and end times.
 *
 * @param {Array} meetings The list of meetings for an offer.
 */
 export const formattedMeetingSchedule = (meetings, session_length_minutes) => {
  if (meetings) {
    return meetings.map(meeting => {
      const time = meeting.time.split('/')[0];
      return { 
        start: new Date(time).toISOString(),
        end: new Date(convertTime(meeting.time, session_length_minutes)).toISOString()
      };
    });
  }
};

/**
 * Returns the formatted end date.
 *
 * @param {String} endDate The end date for a schedule.
 */
 export const formattedEndDate = (endDate) => {
  return formats.shortDate(new Date(endDate));
};

/**
 * Returns the formatted start date.
 *
 * @param {String} endDate The start date for a schedule.
 */
export const formattedStartDate = (startDate) => {
  return formats.shortDate(new Date(startDate));
};

/**
 * Returns the formatted sessions per week.
 *
 * @param {Object} schedule The schedule for an offer.
 */
export const formattedSessionsPerWeek = (schedule) => {
  const totalSessions = schedule.days_of_week_datetime.length;
  // get the difference of the dates in seconds and calculate total weeks
  // (604800 = sesonds per week).
  const weekTotal = parseInt(
    (new Date(schedule.end_date) - new Date(schedule.start_date)) / (604800 * 1000)
  ) + 1;

  return `${maybePluralize(weekTotal, 'week')},` +
  ` ${maybePluralize(totalSessions, 'session')} per week`;
};

/**
 * Returns the formatted total duration for a schedule.
 *
 * @param {Integer} totalHours The total number of hours in a schedule.
 */
export const formattedTotalDuration = (totalHours) => {
  return _sessionMinutesOrHours(totalHours * 60);
};
